import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type {
  AbsenceListItem, AbsenceType, HrMeResponse, LeaveBalanceResponse, SubmitAbsenceResponse,
} from '@ip/shared-types';
import {
  Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/shared/DatePicker';
import { cn } from '@/lib/utils';
import { useHrApi, SessionExpiredError } from '@/services/api';
import {
  ABSENCE_TYPE_META, absenceTypeLabel, eachDay, formatDateRange, formatDaysHours,
  isWeekend, parseYmd, requestableTypes, toYmd, workingHoursBetween,
} from '@/lib/hr';
import { t } from '@/i18n';

interface RequestSheetProps {
  nip: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hrMe: HrMeResponse;
  /** Current-year balance (null for non-UOP — the pool preview is skipped). */
  balance: LeaveBalanceResponse | null;
  /** Map<YYYY-MM-DD, name> covering the selectable range. */
  holidays: Map<string, string>;
  /** Own absences — powers the client-side overlap warning. */
  myAbsences: AbsenceListItem[];
  initialRange: { from: string; to: string } | null;
  onSubmitted: (response: SubmitAbsenceResponse) => void;
}

/** Types with a static statutory hint under the summary. */
const HINTED_TYPES: AbsenceType[] = [
  'occasional', 'childcare_art188', 'care_art173', 'force_majeure_art148',
  'unpaid', 'remote_work', 'unavailability_b2b',
];

/**
 * The absence-request sheet: type (only what this contract may file), a
 * holiday-aware range, half-day toggles, and a LIVE balance preview
 * ("after approval you'll have 8d 4h left") computed with the same
 * working-day rules as the backend. Backend validation errors render inline.
 */
export function RequestSheet({
  nip, open, onOpenChange, hrMe, balance, holidays, myAbsences, initialRange, onSubmitted,
}: RequestSheetProps) {
  const { submitAbsence } = useHrApi();
  const employee = hrMe.employee;
  const norm = employee?.dailyNormHours ?? 8;
  const types = useMemo(() => requestableTypes(employee?.employmentType ?? null), [employee?.employmentType]);
  const isUop = employee?.employmentType === 'employment_contract';

  const [type, setType] = useState<AbsenceType>(types[0]);
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [halfDayStart, setHalfDayStart] = useState(false);
  const [halfDayEnd, setHalfDayEnd] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // Re-seed the form every time the sheet opens.
  useEffect(() => {
    if (!open) return;
    setType(types[0]);
    setFrom(initialRange?.from ?? null);
    setTo(initialRange?.to ?? null);
    setHalfDayStart(false);
    setHalfDayEnd(false);
    setReason('');
    setServerError(null);
    setSubmitting(false);
  }, [open, initialRange, types]);

  const meta = ABSENCE_TYPE_META[type];
  const singleDay = !!from && from === to;
  const rangeValid = !!from && !!to && from <= to;
  const crossYear = rangeValid && from!.slice(0, 4) !== to!.slice(0, 4);

  const hours = rangeValid && !crossYear
    ? workingHoursBetween(from!, to!, new Set(holidays.keys()), {
        halfDayStart, halfDayEnd: singleDay ? false : halfDayEnd, dailyNormHours: norm,
      })
    : 0;
  const days = Math.round((hours / norm) * 100) / 100;

  const freeDaysInRange = rangeValid && !crossYear
    ? eachDay(from!, to!).filter((d) => isWeekend(d) || holidays.has(d)).length
    : 0;

  // The loaded balance is for the CURRENT year — for a range in another year
  // (e.g. planning January in December) skip the client-side pool preview and
  // let the backend (which validates against the range's year) decide.
  const balanceApplies =
    balance !== null && rangeValid && Number(from!.slice(0, 4)) === balance.year ? balance : null;

  const poolCost = meta.pool ? hours : 0;
  const afterHours = balanceApplies ? balanceApplies.remainingHours - poolCost : null;

  const overlap = rangeValid
    ? myAbsences.find(
        (a) => (a.status === 'pending' || a.status === 'approved') && a.dateFrom <= to! && from! <= a.dateTo,
      )
    : undefined;

  const onDemandRemaining = balanceApplies ? Math.max(0, 4 - balanceApplies.onDemandUsedDays) : null;

  const clientError = !rangeValid
    ? null
    : crossYear
      ? t('hr.request.errors.crossYear')
      : hours === 0 && meta.pool
        ? t('hr.request.errors.noWorkingDays')
        : meta.pool && afterHours !== null && afterHours < 0
          ? t('hr.request.errors.insufficient', { amount: formatDaysHours(-afterHours, norm) })
          : type === 'vacation_on_demand' && onDemandRemaining !== null && days > onDemandRemaining
            ? t('hr.request.errors.onDemandCap')
            : null;

  const canSubmit = rangeValid && !crossYear && !clientError && !overlap && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !from || !to) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const response = await submitAbsence(nip, {
        type,
        dateFrom: from,
        dateTo: to,
        halfDayStart: halfDayStart || undefined,
        halfDayEnd: (singleDay ? false : halfDayEnd) || undefined,
        reason: reason.trim() || undefined,
      });
      toast.success(meta.notifyOnly ? t('hr.request.successInfoTitle') : t('hr.request.successTitle'), {
        description: response.balancePreview
          ? t('hr.request.successBalance', {
              amount: formatDaysHours(response.balancePreview.after.remainingHours, norm),
            })
          : formatDateRange(from, to),
      });
      onSubmitted(response);
      onOpenChange(false);
    } catch (error) {
      if (error instanceof SessionExpiredError) return;
      setServerError(error instanceof Error ? error.message : String(error));
    } finally {
      setSubmitting(false);
    }
  };

  const pickerToYmd = (d: Date | null) => (d ? toYmd(new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))) : null);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{isUop ? t('hr.request.title') : t('hr.request.titleB2b')}</SheetTitle>
          <SheetDescription>{t('hr.calendar.hint')}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 pb-4">
          {/* Type */}
          <div className="space-y-1.5">
            <Label htmlFor="hr-type">{t('hr.request.type')}</Label>
            <Select value={type} onValueChange={(v) => setType(v as AbsenceType)}>
              <SelectTrigger id="hr-type" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {isUop ? (
                  <>
                    <SelectGroup>
                      <SelectLabel>{t('hr.request.groupPool')}</SelectLabel>
                      {types.filter((tp) => ABSENCE_TYPE_META[tp].pool).map(renderTypeItem)}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>{t('hr.request.groupOther')}</SelectLabel>
                      {types.filter((tp) => !ABSENCE_TYPE_META[tp].pool && !ABSENCE_TYPE_META[tp].notifyOnly).map(renderTypeItem)}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>{t('hr.request.groupInfo')}</SelectLabel>
                      {types.filter((tp) => ABSENCE_TYPE_META[tp].notifyOnly).map(renderTypeItem)}
                    </SelectGroup>
                  </>
                ) : (
                  types.map(renderTypeItem)
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Range */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t('hr.request.dateFrom')}</Label>
              <DatePicker
                value={from ? parseYmd(from) : null}
                onChange={(d) => {
                  const v = pickerToYmd(d);
                  setFrom(v);
                  if (v && (!to || to < v)) setTo(v);
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('hr.request.dateTo')}</Label>
              <DatePicker
                value={to ? parseYmd(to) : null}
                onChange={(d) => {
                  const v = pickerToYmd(d);
                  setTo(v);
                  if (v && (!from || from > v)) setFrom(v);
                }}
              />
            </div>
          </div>

          {/* Half days */}
          {rangeValid && (
            <div className="space-y-2">
              {singleDay ? (
                <label className="flex items-center justify-between gap-2 text-sm">
                  {t('hr.request.halfDay', { hours: norm / 2 })}
                  <Switch checked={halfDayStart} onCheckedChange={setHalfDayStart} />
                </label>
              ) : (
                <>
                  <label className="flex items-center justify-between gap-2 text-sm">
                    {t('hr.request.halfDayStart')}
                    <Switch checked={halfDayStart} onCheckedChange={setHalfDayStart} />
                  </label>
                  <label className="flex items-center justify-between gap-2 text-sm">
                    {t('hr.request.halfDayEnd')}
                    <Switch checked={halfDayEnd} onCheckedChange={setHalfDayEnd} />
                  </label>
                </>
              )}
            </div>
          )}

          {/* Reason */}
          <div className="space-y-1.5">
            <Label htmlFor="hr-reason">{t('hr.request.reason')}</Label>
            <Textarea
              id="hr-reason"
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={type === 'occasional' ? t('hr.request.reasonOccasionalHint') : undefined}
            />
          </div>

          {/* Live summary */}
          {rangeValid && !crossYear && (
            <div className="bg-muted/40 space-y-2 rounded-lg border p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('hr.request.workingDays')}</span>
                <span className="font-medium tabular-nums">
                  {t('hr.request.workingDaysValue', { days, hours })}
                </span>
              </div>
              {freeDaysInRange > 0 && (
                <p className="text-muted-foreground text-xs">
                  {t('hr.request.freeDaysExcluded', { count: freeDaysInRange })}
                </p>
              )}
              {meta.pool && balanceApplies && afterHours !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t('hr.request.afterApproval')}</span>
                  <span className={cn('flex items-center gap-1.5 font-semibold tabular-nums', afterHours < 0 && 'text-destructive')}>
                    <span className="text-muted-foreground font-normal">{formatDaysHours(balanceApplies.remainingHours, norm)}</span>
                    <ArrowRight className="text-muted-foreground size-3.5" />
                    {formatDaysHours(afterHours, norm)}
                  </span>
                </div>
              )}
              {type === 'vacation_on_demand' && balanceApplies && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t('hr.request.onDemandCounter')}</span>
                  <span className="font-medium tabular-nums">{balanceApplies.onDemandUsedDays}/4</span>
                </div>
              )}
              {HINTED_TYPES.includes(type) && (
                <p className="text-muted-foreground text-xs">{t(`hr.request.hints.${type}`)}</p>
              )}
            </div>
          )}

          {overlap && (
            <Alert className="border-amber-500/50 text-amber-700 dark:text-amber-400 [&>svg]:text-amber-600">
              <AlertTriangle className="size-4" />
              <AlertDescription className="text-amber-700 dark:text-amber-400">
                {t('hr.request.overlapWarning', { range: formatDateRange(overlap.dateFrom, overlap.dateTo) })}
              </AlertDescription>
            </Alert>
          )}

          {clientError && (
            <Alert variant="destructive">
              <AlertTriangle className="size-4" />
              <AlertDescription>{clientError}</AlertDescription>
            </Alert>
          )}

          {serverError && (
            <Alert variant="destructive">
              <AlertTriangle className="size-4" />
              <AlertDescription>{serverError}</AlertDescription>
            </Alert>
          )}
        </div>

        <SheetFooter className="flex-row justify-end gap-2 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t('hr.request.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting && <Loader2 className="size-4 animate-spin" />}
            {meta.notifyOnly ? t('hr.request.submitInfo') : t('hr.request.submit')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function renderTypeItem(type: AbsenceType) {
  const meta = ABSENCE_TYPE_META[type];
  return (
    <SelectItem key={type} value={type}>
      <span className="flex items-center gap-2">
        <span className={cn('size-2 shrink-0 rounded-full', meta.dot)} />
        {absenceTypeLabel(type)}
      </span>
    </SelectItem>
  );
}
